/**
 * Sign up page.
 */

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import './sign-in.scss'; // Reuse sign-in styles

export function SignUp() {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    // Validate password strength
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      setIsLoading(false);
      return;
    }

    try {
      await signUp(email, password);
      setSuccess(true);
      // After successful signup, redirect to sign in after 3 seconds
      setTimeout(() => {
        navigate('/auth/sign-in');
      }, 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create account');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="sign-in">
        <Card className="sign-in__card">
          <div className="sign-in__header">
            <h1 className="h1 brand brand--spark">Origyns</h1>
            <p className="of-text-muted">Account created successfully!</p>
            <p className="of-text-muted" style={{ marginTop: 'var(--of-space-2)' }}>
              Please check your email to verify your account, then sign in.
            </p>
            <p className="of-text-muted" style={{ marginTop: 'var(--of-space-4)' }}>
              Redirecting to sign in...
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="sign-in">
      <Card className="sign-in__card">
        <div className="sign-in__header">
          <h1 className="h1 brand brand--spark">Origyns</h1>
          <p className="of-text-muted">Create your account</p>
        </div>

        <form onSubmit={handleSubmit} className="sign-in__form">
          {error && (
            <div className="sign-in__error">{error}</div>
          )}

          <div className="sign-in__field">
            <label htmlFor="email" className="label">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              required
              disabled={isLoading}
              placeholder="you@example.com"
            />
          </div>

          <div className="sign-in__field">
            <label htmlFor="password" className="label">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              required
              disabled={isLoading}
              placeholder="At least 8 characters"
              minLength={8}
            />
          </div>

          <div className="sign-in__field">
            <label htmlFor="confirmPassword" className="label">
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="input"
              required
              disabled={isLoading}
              placeholder="Re-enter your password"
              minLength={8}
            />
          </div>

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? 'Creating account...' : 'Create account'}
          </Button>

          <div style={{ textAlign: 'center', marginTop: 'var(--of-space-4)' }}>
            <p className="of-text-muted">
              Already have an account?{' '}
              <Link 
                to="/auth/sign-in" 
                style={{ color: 'var(--of-flow-blue)', textDecoration: 'none' }}
              >
                Sign in
              </Link>
            </p>
          </div>
        </form>
      </Card>
    </div>
  );
}

